import React from "react";
import { Button as MuiButton, CircularProgress } from "@mui/material";

/**
 * Thin wrapper around MUI Button. Does not reimplement styles — relies on the
 * MUI theme for color + the global MuiButton override for pill shape.
 *
 * Props:
 *   variant: "primary" | "secondary" | "ghost" | "danger" (default "primary")
 *   size:    "sm" | "md" | "lg" (default "md")
 *   loading: boolean — disables the button and shows a small spinner to the left
 *   leftIcon / rightIcon: ReactNode
 *   fullWidth, plus any standard MUI Button prop
 */

const VARIANT_MAP = {
  primary: { variant: "contained", color: "primary" },
  secondary: { variant: "outlined", color: "primary" },
  ghost: { variant: "text", color: "inherit" },
  danger: { variant: "contained", color: "error" },
};

const SIZE_MAP = {
  sm: { py: 0.5, px: 2, fontSize: 13 },
  md: { py: 1, px: 3, fontSize: 14 },
  lg: { py: 1.25, px: 4, fontSize: 15 },
};

const GHOST_SX = {
  color: "#667085",
  "&:hover": { backgroundColor: "rgba(102,112,133,0.08)" },
};

const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    disabled,
    children,
    sx,
    ...rest
  },
  ref
) {
  const mapped = VARIANT_MAP[variant] || VARIANT_MAP.primary;
  const sizeSx = SIZE_MAP[size] || SIZE_MAP.md;
  const ghostSx = variant === "ghost" ? GHOST_SX : null;

  const mergedSx = {
    ...sizeSx,
    ...(ghostSx || {}),
    transition: "transform 0.1s ease-out",
    "&:active": { transform: "scale(0.98)" },
    ...(sx || {}),
  };

  return (
    <MuiButton
      ref={ref}
      variant={mapped.variant}
      color={mapped.color}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={
        loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          leftIcon || undefined
        )
      }
      endIcon={!loading ? rightIcon || undefined : undefined}
      sx={mergedSx}
      {...rest}
    >
      {children}
    </MuiButton>
  );
});

export default Button;
